import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import BookingForm from '../components/BookingForm';
import { api } from '../api/axiosClient';
import { ArrowLeft } from 'lucide-react';
import { setCurrentBooking, updateBookingStatus } from '../store/bookingSlice';
import { addBooking } from '../store/userSlice';

const Booking = () => {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user, isAuthenticated } = useSelector((state) => state.user);

  useEffect(() => {
    // Fetch partner details (mock for now)
    const fetchPartner = async () => {
      const data = await api.getPartners(0, 0);
      const selectedPartner = data.partners.find(
        (p) => p.id === parseInt(partnerId)
      );
      setPartner(selectedPartner);
    };

    fetchPartner();
  }, [partnerId]);

  const handleSubmit = async (formData) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Save booking intent to localStorage
      localStorage.setItem('pendingBooking', JSON.stringify({ formData, partnerId }));
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        ...formData,
        partnerId: parseInt(partnerId),
        partnerName: partner.name,
        userId: user?.id,
        status: 'pending_partner_acceptance',
      };

      const response = await api.createBooking(bookingData);
      
      // Save booking to Redux
      dispatch(setCurrentBooking(response.booking));
      dispatch(addBooking(response.booking));
      
      // Save booking data to localStorage
      localStorage.setItem('lastBooking', JSON.stringify(response.booking));
      
      // Navigate to success page
      navigate('/success');
    } catch (error) {
      console.error('Booking error:', error);
      alert('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!partner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-accent to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-600">Loading partner details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-accent to-purple-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/nearby')}
          className="flex items-center gap-2 mb-6 text-gray-700 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Therapists</span>
        </motion.button>

        {/* Partner Info Card */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl p-6 shadow-lg mb-8"
        >
          <div className="flex items-center gap-4">
            <img
              src={partner.image}
              alt={partner.name}
              className="w-20 h-20 rounded-full object-cover"
            />
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{partner.name}</h2>
              <p className="text-gray-600">{partner.specialty}</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={`${
                        i < Math.floor(partner.rating)
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
                <span className="text-sm text-gray-600">
                  {partner.rating} ({partner.reviews} reviews)
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Booking Form */}
        <BookingForm partner={partner} onSubmit={handleSubmit} loading={loading} />

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-8 bg-primary/10 rounded-2xl p-6"
        >
          <h3 className="font-semibold text-gray-800 mb-3">What to Expect:</h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">✓</span>
              <span>16-hour holistic cleansing therapy session</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">✓</span>
              <span>Personalized detox plan based on your needs</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">✓</span>
              <span>Follow-up consultation included</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-1">✓</span>
              <span>Natural, safe, and effective methods</span>
            </li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default Booking;
